import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Pencil, Ticket as TicketIcon, CheckCircle2, FileDown, RefreshCw } from "lucide-react";

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-border last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground col-span-1">{label}</span>
      <span className={`col-span-2 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</span>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [meta, setMeta] = useState({ countries: ["USA", "Canada"], sale_statuses: [] });
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketDesc, setTicketDesc] = useState("");
  const [noteInputs, setNoteInputs] = useState({});

  const load = () => api.get(`/customers/${id}`).then(({ data }) => setC(data)).catch(() => setC(false));

  useEffect(() => {
    load();
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (c === null) return <p className="font-mono text-sm text-muted-foreground">Loading…</p>;
  if (c === false) return <p className="text-muted-foreground">Customer not found.</p>;

  const isSales = user?.role === "sales";
  const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
  const fullName = [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");

  const openEdit = () => {
    setEdit({
      first_name: c.first_name, middle_name: c.middle_name, last_name: c.last_name,
      contact_number: c.contact_number, primary_issue: c.primary_issue, email: c.email || "",
      address1: c.address1, address2: c.address2, city: c.city, state: c.state,
      zip_code: c.zip_code, country: c.country || "", sale_amount: c.sale_amount ?? "",
      plan_purchased: c.plan_purchased, sale_status: c.sale_status || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const payload = { ...edit };
    if (payload.sale_amount === "") delete payload.sale_amount;
    else payload.sale_amount = parseFloat(payload.sale_amount);
    if (!payload.country) delete payload.country;
    if (!payload.sale_status) delete payload.sale_status;
    try {
      await api.put(`/customers/${id}`, payload);
      toast.success("Customer updated.");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addTicket = async () => {
    try {
      await api.post("/tickets", { customer_id: c.id, description: ticketDesc });
      toast.success("Ticket created.");
      setTicketOpen(false);
      setTicketDesc("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const closeTicket = async (tid) => {
    try {
      await api.put(`/tickets/${tid}/close`);
      toast.success("Ticket closed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resendAgreement = async () => {
    try {
      const { data } = await api.post(`/customers/${id}/resend-agreement`);
      toast.success(
        data.signature_status === "sent"
          ? "Agreement re-sent to the customer for signature."
          : "DocuSign is not configured yet — request marked pending setup."
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadDoc = async (kind) => {
    try {
      const res = await api.get(`/customers/${id}/documents/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.customer_id}_${kind === "signed" ? "agreement" : "audit_certificate"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Document not available yet.");
    }
  };

  const addNote = async (tid) => {
    const text = (noteInputs[tid] || "").trim();
    if (!text) return;
    try {
      await api.post(`/tickets/${tid}/notes`, { text });
      setNoteInputs((s) => ({ ...s, [tid]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-4xl">
      <Button variant="ghost" className="rounded-none mb-4 -ml-2" onClick={() => navigate("/customers")}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to customers
      </Button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="font-mono text-primary text-sm">{c.customer_id}</div>
          <h1 className="text-4xl">{fullName}</h1>
          <div className="flex gap-2 mt-2">
            <StatusBadge value={c.call_converted ? "Yes" : "No"} />
            {c.sale_status && <StatusBadge value={c.sale_status} />}
            <StatusBadge value={c.signature_status} />
          </div>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button data-testid="edit-customer-btn" className="rounded-none" onClick={openEdit}>
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
          {user?.role === "admin" && c.call_converted && c.signature_status !== "completed" && (
            <Button variant="outline" data-testid="resend-agreement-btn" className="rounded-none" onClick={resendAgreement}>
              <RefreshCw className="h-4 w-4 mr-2" /> Resend Agreement
            </Button>
          )}
          {(user?.role === "technician" || user?.role === "admin") && (
            <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-ticket-btn" className="rounded-none">
                  <TicketIcon className="h-4 w-4 mr-2" /> Add Ticket
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle>New Ticket for {c.customer_id}</DialogTitle>
                </DialogHeader>
                <Textarea
                  data-testid="new-ticket-desc"
                  className="rounded-none min-h-28"
                  placeholder="Describe the issue…"
                  value={ticketDesc}
                  onChange={(e) => setTicketDesc(e.target.value)}
                />
                <DialogFooter>
                  <Button data-testid="save-ticket-btn" className="rounded-none" onClick={addTicket} disabled={!ticketDesc.trim()}>
                    Create Ticket
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="border border-border bg-card p-6 mb-6">
        <h2 className="text-lg mb-3">Details</h2>
        {isSales ? (
          <>
            <Row label="Full Name" value={fullName} />
            <Row label="Sale Amount" value={money(c.sale_amount)} mono />
            <Row label="Sale Status" value={c.sale_status || "—"} />
            <Row label="Tickets" value={c.ticket_count} mono />
            <p className="text-xs text-muted-foreground mt-3">
              Sales role has limited visibility. Contact an administrator for full records.
            </p>
          </>
        ) : (
          <>
            <Row label="Contact Number" value={c.contact_number} mono />
            <Row label="Primary Issue" value={c.primary_issue} />
            <Row label="Email" value={c.email} mono />
            <Row label="Address 1" value={c.address1} />
            <Row label="Address 2" value={c.address2} />
            <Row label="City / State" value={[c.city, c.state].filter(Boolean).join(", ")} />
            <Row label="Zip Code" value={c.zip_code} mono />
            <Row label="Country" value={c.country} />
            <Row label="Sale Amount" value={money(c.sale_amount)} mono />
            <Row label="Plan Purchased" value={c.plan_purchased} />
            <Row label="Sale Status" value={c.sale_status} />
            <Row label="Signature Status" value={<StatusBadge value={c.signature_status} />} />
            {!c.call_converted && <Row label="Not Converted Reason" value={c.not_converted_reason} />}
            <Row label="Created By" value={c.created_by_name} />
          </>
        )}
      </div>

      {/* Signed documents (DocuSign audit) */}
      {c.has_signed_docs && !isSales && (
        <div className="border border-border bg-card p-6 mb-6">
          <h2 className="text-lg mb-3">Signed Documents</h2>
          <div className="flex gap-3 flex-wrap">
            <Button variant="outline" data-testid="download-signed-btn" className="rounded-none" onClick={() => downloadDoc("signed")}>
              <FileDown className="h-4 w-4 mr-2" /> Signed Agreement (PDF)
            </Button>
            <Button variant="outline" data-testid="download-certificate-btn" className="rounded-none" onClick={() => downloadDoc("certificate")}>
              <FileDown className="h-4 w-4 mr-2" /> Audit Certificate (PDF)
            </Button>
          </div>
        </div>
      )}

      {/* Tickets */}
      <div className="border border-border bg-card p-6">
        <h2 className="text-lg mb-3">Tickets ({c.tickets?.length || 0})</h2>
        {!c.tickets || c.tickets.length === 0 ? (
          <p className="text-muted-foreground text-sm">No tickets for this customer.</p>
        ) : (
          <div className="space-y-3">
            {c.tickets.map((t) => (
              <div key={t.id} data-testid={`ticket-${t.ticket_id}`} className="border border-border p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-primary text-sm">{t.ticket_id}</span>
                      <StatusBadge value={t.status} />
                    </div>
                    <p className="mt-1 text-sm">{t.description}</p>
                    <p className="text-xs text-muted-foreground mt-2 font-mono">
                      Assigned: {t.assigned_to_name}
                      {t.status === "Closed" && t.closed_at && ` · Closed ${new Date(t.closed_at).toLocaleString()} by ${t.closed_by}`}
                    </p>
                  </div>
                  {t.status === "Active" && (user?.role === "technician" || user?.role === "admin") && (
                    <Button
                      size="sm"
                      data-testid={`close-ticket-${t.ticket_id}`}
                      className="rounded-none"
                      onClick={() => closeTicket(t.id)}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" /> Close Ticket
                    </Button>
                  )}
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                    Progress Notes ({t.notes?.length || 0})
                  </div>
                  {t.notes && t.notes.length > 0 && (
                    <ul className="space-y-2 mb-3">
                      {t.notes.map((n) => (
                        <li key={n.id} data-testid={`note-${n.id}`} className="text-sm border-l-2 border-primary pl-3">
                          <div>{n.text}</div>
                          <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                            {n.author} ({n.author_role}) · {new Date(n.created_at).toLocaleString()}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(user?.role === "technician" || user?.role === "admin") && (
                    <div className="flex gap-2">
                      <Input
                        data-testid={`note-input-${t.ticket_id}`}
                        className="rounded-none"
                        placeholder="Add a progress note…"
                        value={noteInputs[t.id] || ""}
                        onChange={(e) => setNoteInputs((s) => ({ ...s, [t.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") addNote(t.id); }}
                      />
                      <Button
                        size="sm"
                        data-testid={`add-note-${t.ticket_id}`}
                        className="rounded-none"
                        onClick={() => addNote(t.id)}
                        disabled={!(noteInputs[t.id] || "").trim()}
                      >
                        Log
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit dialog (admin) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {c.customer_id}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {[
              ["first_name", "First Name"], ["middle_name", "Middle Name"], ["last_name", "Last Name"],
              ["contact_number", "Contact"], ["email", "Email"], ["primary_issue", "Primary Issue"],
              ["address1", "Address 1"], ["address2", "Address 2"], ["city", "City"], ["state", "State"],
              ["zip_code", "Zip"], ["plan_purchased", "Plan"], ["sale_amount", "Sale Amount"],
            ].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input
                  data-testid={`edit-${k}`}
                  className="rounded-none"
                  value={edit[k] ?? ""}
                  onChange={(e) => setEdit((s) => ({ ...s, [k]: e.target.value }))}
                />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs">Country</Label>
              <Select value={edit.country} onValueChange={(v) => setEdit((s) => ({ ...s, country: v }))}>
                <SelectTrigger data-testid="edit-country" className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {meta.countries.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sale Status</Label>
              <Select value={edit.sale_status} onValueChange={(v) => setEdit((s) => ({ ...s, sale_status: v }))}>
                <SelectTrigger data-testid="edit-sale-status" className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {(meta.sale_statuses || []).map((x) => <SelectItem key={x} value={x} data-testid={`sale-status-${x}`}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-edit-btn" className="rounded-none" onClick={saveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
